import React, { createContext, useContext, useReducer, useCallback } from 'react';
import inventoryApi from '../services/inventoryApi';
import adminApi from '../services/adminApi';

const InventoryContext = createContext({
  isLoading: true, error: null,
  categories: [], items: [], suppliers: [], warehouses: [], stockLevels: [],
  fetchCategories: async () => {}, fetchCategoryTree: async () => {},
  createCategory: async () => {}, updateCategory: async () => {}, deleteCategory: async () => {},
  fetchItems: async () => {}, fetchItemById: async () => {}, createItem: async () => {},
  updateItem: async () => {}, deleteItem: async () => {}, searchItems: async () => {},
  fetchSuppliers: async () => {}, createSupplier: async () => {},
  updateSupplier: async () => {}, deleteSupplier: async () => {},
  fetchWarehouses: async () => {}, fetchStockLevels: async () => {},
});

// Action types
const INVENTORY_ACTIONS = {
  // Loading states
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  
  // Categories
  SET_CATEGORIES: 'SET_CATEGORIES',
  ADD_CATEGORY: 'ADD_CATEGORY',
  UPDATE_CATEGORY: 'UPDATE_CATEGORY',
  DELETE_CATEGORY: 'DELETE_CATEGORY',
  SET_CATEGORY_TREE: 'SET_CATEGORY_TREE',
  
  // Items
  SET_ITEMS: 'SET_ITEMS',
  ADD_ITEM: 'ADD_ITEM',
  UPDATE_ITEM: 'UPDATE_ITEM',
  DELETE_ITEM: 'DELETE_ITEM',
  SET_SELECTED_ITEM: 'SET_SELECTED_ITEM',
  
  // Suppliers
  SET_SUPPLIERS: 'SET_SUPPLIERS',
  ADD_SUPPLIER: 'ADD_SUPPLIER',
  UPDATE_SUPPLIER: 'UPDATE_SUPPLIER',
  DELETE_SUPPLIER: 'DELETE_SUPPLIER',
  
  // Warehouses
  SET_WAREHOUSES: 'SET_WAREHOUSES',
  ADD_WAREHOUSE: 'ADD_WAREHOUSE',
  UPDATE_WAREHOUSE: 'UPDATE_WAREHOUSE',
  DELETE_WAREHOUSE: 'DELETE_WAREHOUSE',
  
  // Stock
  SET_STOCK_LEVELS: 'SET_STOCK_LEVELS',
  UPDATE_STOCK_LEVEL: 'UPDATE_STOCK_LEVEL',
  SET_LOW_STOCK_ITEMS: 'SET_LOW_STOCK_ITEMS',
  
  // Movements
  SET_MOVEMENTS: 'SET_MOVEMENTS',
  ADD_MOVEMENT: 'ADD_MOVEMENT',
  UPDATE_MOVEMENT: 'UPDATE_MOVEMENT',
  
  // Purchase Orders
  SET_PURCHASE_ORDERS: 'SET_PURCHASE_ORDERS',
  ADD_PURCHASE_ORDER: 'ADD_PURCHASE_ORDER',
  UPDATE_PURCHASE_ORDER: 'UPDATE_PURCHASE_ORDER',
  DELETE_PURCHASE_ORDER: 'DELETE_PURCHASE_ORDER',
  
  // Goods Transfer Requests
  SET_GOODS_TRANSFER_REQUESTS: 'SET_GOODS_TRANSFER_REQUESTS',
  ADD_GOODS_TRANSFER_REQUEST: 'ADD_GOODS_TRANSFER_REQUEST',
  UPDATE_GOODS_TRANSFER_REQUEST: 'UPDATE_GOODS_TRANSFER_REQUEST',
  DELETE_GOODS_TRANSFER_REQUEST: 'DELETE_GOODS_TRANSFER_REQUEST',

  // Goods Issue Notes
  SET_GOODS_ISSUE_NOTES: 'SET_GOODS_ISSUE_NOTES',
  ADD_GOODS_ISSUE_NOTE: 'ADD_GOODS_ISSUE_NOTE',
  UPDATE_GOODS_ISSUE_NOTE: 'UPDATE_GOODS_ISSUE_NOTE',
  DELETE_GOODS_ISSUE_NOTE: 'DELETE_GOODS_ISSUE_NOTE',

  // Transfer In
  SET_TRANSFER_INS: 'SET_TRANSFER_INS',
  ADD_TRANSFER_IN: 'ADD_TRANSFER_IN',
  UPDATE_TRANSFER_IN: 'UPDATE_TRANSFER_IN',

  // Dashboard
  SET_DASHBOARD_DATA: 'SET_DASHBOARD_DATA',
  
  // Assets
  SET_ASSETS: 'SET_ASSETS',
  ADD_ASSET: 'ADD_ASSET',
  UPDATE_ASSET: 'UPDATE_ASSET',
  DELETE_ASSET: 'DELETE_ASSET',
  
  // Audits
  SET_AUDITS: 'SET_AUDITS',
  ADD_AUDIT: 'ADD_AUDIT',
  UPDATE_AUDIT: 'UPDATE_AUDIT',
  
  // Filters and pagination
  SET_FILTERS: 'SET_FILTERS',
  SET_PAGINATION: 'SET_PAGINATION',
};

// Initial state
const initialState = {
  // Loading and error states
  loading: {
    categories: false,
    items: false,
    suppliers: false,
    warehouses: false,
    stock: false,
    movements: false,
    purchaseOrders: false,
    goodsTransferRequests: false,
    dashboard: false,
    assets: false,
    audits: false,
  },
  error: null,
  
  // Data
  categories: [],
  categoryTree: [],
  items: [],
  selectedItem: null,
  suppliers: [],
  warehouses: [],
  stockLevels: [],
  lowStockItems: [],
  movements: [],
  purchaseOrders: [],
  goodsTransferRequests: [],
  goodsIssueNotes: [],
  transferIns: [],
  dashboardData: null,
  assets: [],
  audits: [],
  
  // UI state
  filters: {
    categories: {},
    items: {},
    suppliers: {},
    warehouses: {},
    stock: {},
    movements: {},
    purchaseOrders: {},
    goodsTransferRequests: {},
    goodsIssueNotes: {},
    transferIns: {},
    assets: {},
    audits: {},
  },
  pagination: {
    categories: { page: 1, limit: 20, total: 0 },
    items: { page: 1, limit: 20, total: 0 },
    suppliers: { page: 1, limit: 20, total: 0 },
    warehouses: { page: 1, limit: 20, total: 0 },
    stock: { page: 1, limit: 20, total: 0 },
    movements: { page: 1, limit: 20, total: 0 },
    purchaseOrders: { page: 1, limit: 20, total: 0 },
    goodsTransferRequests: { page: 1, limit: 20, total: 0 },
    goodsIssueNotes: { page: 1, limit: 20, total: 0 },
    transferIns: { page: 1, limit: 20, total: 0 },
    assets: { page: 1, limit: 20, total: 0 },
    audits: { page: 1, limit: 20, total: 0 },
  },
};

// Reducer
function inventoryReducer(state, action) {
  switch (action.type) {
    case INVENTORY_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.payload.key]: action.payload.value,
        },
      };
      
    case INVENTORY_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: Object.keys(state.loading).reduce((acc, key) => {
          acc[key] = false;
          return acc;
        }, {}),
      };
      
    case INVENTORY_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };
      
    // Categories
    case INVENTORY_ACTIONS.SET_CATEGORIES:
      return {
        ...state,
        categories: action.payload.data,
        pagination: {
          ...state.pagination,
          categories: {
            ...state.pagination.categories,
            ...action.payload.pagination,
          },
        },
      };
      
    case INVENTORY_ACTIONS.ADD_CATEGORY:
      return {
        ...state,
        categories: [action.payload, ...state.categories],
      };
      
    case INVENTORY_ACTIONS.UPDATE_CATEGORY:
      return {
        ...state,
        categories: state.categories.map(cat =>
          cat._id === action.payload._id ? action.payload : cat
        ),
      };
      
    case INVENTORY_ACTIONS.DELETE_CATEGORY:
      return {
        ...state,
        categories: state.categories.filter(cat => cat._id !== action.payload),
      };
      
    case INVENTORY_ACTIONS.SET_CATEGORY_TREE:
      return {
        ...state,
        categoryTree: action.payload,
      };
      
    // Items
    case INVENTORY_ACTIONS.SET_ITEMS:
      return {
        ...state,
        items: action.payload.data,
        pagination: {
          ...state.pagination,
          items: {
            ...state.pagination.items,
            ...action.payload.pagination,
          },
        },
      };
      
    case INVENTORY_ACTIONS.ADD_ITEM:
      return {
        ...state,
        items: [action.payload, ...state.items],
      };
      
    case INVENTORY_ACTIONS.UPDATE_ITEM:
      return {
        ...state,
        items: state.items.map(item =>
          item._id === action.payload._id ? action.payload : item
        ),
        selectedItem: state.selectedItem && state.selectedItem._id === action.payload._id 
          ? action.payload 
          : state.selectedItem,
      };
      
    case INVENTORY_ACTIONS.DELETE_ITEM:
      return {
        ...state,
        items: state.items.filter(item => item._id !== action.payload),
        selectedItem: state.selectedItem && state.selectedItem._id === action.payload 
          ? null 
          : state.selectedItem,
      };
      
    case INVENTORY_ACTIONS.SET_SELECTED_ITEM:
      return {
        ...state,
        selectedItem: action.payload,
      };
      
    // Suppliers
    case INVENTORY_ACTIONS.SET_SUPPLIERS:
      return {
        ...state,
        suppliers: action.payload.data,
        pagination: {
          ...state.pagination,
          suppliers: {
            ...state.pagination.suppliers,
            ...action.payload.pagination,
          },
        },
      };
      
    case INVENTORY_ACTIONS.ADD_SUPPLIER:
      return {
        ...state,
        suppliers: [action.payload, ...state.suppliers],
      };
      
    case INVENTORY_ACTIONS.UPDATE_SUPPLIER:
      return {
        ...state,
        suppliers: state.suppliers.map(supplier =>
          supplier._id === action.payload._id ? action.payload : supplier
        ),
      };
      
    case INVENTORY_ACTIONS.DELETE_SUPPLIER:
      return {
        ...state,
        suppliers: state.suppliers.filter(supplier => supplier._id !== action.payload),
      };
      
    // Warehouses
    case INVENTORY_ACTIONS.SET_WAREHOUSES:
      return {
        ...state,
        warehouses: action.payload.data,
        pagination: {
          ...state.pagination,
          warehouses: {
            ...state.pagination.warehouses,
            ...action.payload.pagination,
          },
        },
      };
      
    case INVENTORY_ACTIONS.ADD_WAREHOUSE:
      return {
        ...state,
        warehouses: [action.payload, ...state.warehouses],
      };
      
    case INVENTORY_ACTIONS.UPDATE_WAREHOUSE:
      return {
        ...state,
        warehouses: state.warehouses.map(warehouse =>
          warehouse._id === action.payload._id ? action.payload : warehouse
        ),
      };
      
    case INVENTORY_ACTIONS.DELETE_WAREHOUSE:
      return {
        ...state,
        warehouses: state.warehouses.filter(warehouse => warehouse._id !== action.payload),
      };
      
    // Stock
    case INVENTORY_ACTIONS.SET_STOCK_LEVELS:
      return {
        ...state,
        stockLevels: action.payload.data,
        pagination: {
          ...state.pagination,
          stock: {
            ...state.pagination.stock,
            ...action.payload.pagination,
          },
        },
      };
      
    case INVENTORY_ACTIONS.UPDATE_STOCK_LEVEL:
      return {
        ...state,
        stockLevels: state.stockLevels.map(stock =>
          stock._id === action.payload._id ? action.payload : stock
        ),
      };
      
    case INVENTORY_ACTIONS.SET_LOW_STOCK_ITEMS:
      return {
        ...state,
        lowStockItems: action.payload,
      };
      
    // Movements
    case INVENTORY_ACTIONS.SET_MOVEMENTS:
      return {
        ...state,
        movements: action.payload.data,
        pagination: {
          ...state.pagination,
          movements: {
            ...state.pagination.movements,
            ...action.payload.pagination,
          },
        },
      };
      
    case INVENTORY_ACTIONS.ADD_MOVEMENT:
      return {
        ...state,
        movements: [action.payload, ...state.movements],
      };
      
    case INVENTORY_ACTIONS.UPDATE_MOVEMENT:
      return {
        ...state,
        movements: state.movements.map(movement =>
          movement._id === action.payload._id ? action.payload : movement
        ),
      };
      
    // Purchase Orders
    case INVENTORY_ACTIONS.SET_PURCHASE_ORDERS:
      return {
        ...state,
        purchaseOrders: action.payload.data,
        pagination: {
          ...state.pagination,
          purchaseOrders: {
            ...state.pagination.purchaseOrders,
            ...action.payload.pagination,
          },
        },
      };
      
    case INVENTORY_ACTIONS.ADD_PURCHASE_ORDER:
      return {
        ...state,
        purchaseOrders: [action.payload, ...state.purchaseOrders],
      };
      
    case INVENTORY_ACTIONS.UPDATE_PURCHASE_ORDER:
      return {
        ...state,
        purchaseOrders: state.purchaseOrders.map(po =>
          po._id === action.payload._id ? action.payload : po
        ),
      };
      
    case INVENTORY_ACTIONS.DELETE_PURCHASE_ORDER:
      return {
        ...state,
        purchaseOrders: state.purchaseOrders.filter(po => po._id !== action.payload),
      };

    // Goods Transfer Requests
    case INVENTORY_ACTIONS.SET_GOODS_TRANSFER_REQUESTS:
      return {
        ...state,
        goodsTransferRequests: action.payload.data,
        pagination: {
          ...state.pagination,
          goodsTransferRequests: {
            page: action.payload.page || 1,
            limit: action.payload.limit || 20,
            total: action.payload.total || 0,
          },
        },
      };

    case INVENTORY_ACTIONS.ADD_GOODS_TRANSFER_REQUEST:
      return {
        ...state,
        goodsTransferRequests: [action.payload, ...state.goodsTransferRequests],
      };

    case INVENTORY_ACTIONS.UPDATE_GOODS_TRANSFER_REQUEST:
      return {
        ...state,
        goodsTransferRequests: state.goodsTransferRequests.map(gtr => 
          gtr._id === action.payload._id ? action.payload : gtr
        ),
      };

    case INVENTORY_ACTIONS.DELETE_GOODS_TRANSFER_REQUEST:
      return {
        ...state,
        goodsTransferRequests: state.goodsTransferRequests.filter(gtr => gtr._id !== action.payload),
      };

    // Goods Issue Notes
    case INVENTORY_ACTIONS.SET_GOODS_ISSUE_NOTES:
      return {
        ...state,
        goodsIssueNotes: action.payload.data,
        pagination: {
          ...state.pagination,
          goodsIssueNotes: {
            page: action.payload.pagination?.page || 1,
            limit: action.payload.pagination?.limit || 20,
            total: action.payload.pagination?.total || 0,
          },
        },
      };

    case INVENTORY_ACTIONS.ADD_GOODS_ISSUE_NOTE:
      return {
        ...state,
        goodsIssueNotes: [action.payload, ...state.goodsIssueNotes],
      };

    case INVENTORY_ACTIONS.UPDATE_GOODS_ISSUE_NOTE:
      return {
        ...state,
        goodsIssueNotes: state.goodsIssueNotes.map(gin =>
          gin._id === action.payload._id ? action.payload : gin
        ),
      };

    case INVENTORY_ACTIONS.DELETE_GOODS_ISSUE_NOTE:
      return {
        ...state,
        goodsIssueNotes: state.goodsIssueNotes.filter(gin => gin._id !== action.payload),
      };

    // Transfer In
    case INVENTORY_ACTIONS.SET_TRANSFER_INS:
      return {
        ...state,
        transferIns: action.payload.data,
        pagination: {
          ...state.pagination,
          transferIns: {
            page: action.payload.pagination?.page || 1,
            limit: action.payload.pagination?.limit || 20,
            total: action.payload.pagination?.total || 0,
          },
        },
      };

    case INVENTORY_ACTIONS.ADD_TRANSFER_IN:
      return {
        ...state,
        transferIns: [action.payload, ...state.transferIns],
      };

    case INVENTORY_ACTIONS.UPDATE_TRANSFER_IN:
      return {
        ...state,
        transferIns: state.transferIns.map(ti =>
          ti._id === action.payload._id ? action.payload : ti
        ),
      };


    // Dashboard
    case INVENTORY_ACTIONS.SET_DASHBOARD_DATA:
      return {
        ...state,
        dashboardData: action.payload,
      };
      
    // Assets
    case INVENTORY_ACTIONS.SET_ASSETS:
      return {
        ...state,
        assets: action.payload.data,
        pagination: {
          ...state.pagination,
          assets: {
            ...state.pagination.assets,
            ...action.payload.pagination,
          },
        },
      };
      
    case INVENTORY_ACTIONS.ADD_ASSET:
      return {
        ...state,
        assets: [action.payload, ...state.assets],
      };
      
    case INVENTORY_ACTIONS.UPDATE_ASSET:
      return {
        ...state,
        assets: state.assets.map(asset =>
          asset._id === action.payload._id ? action.payload : asset
        ),
      };
      
    case INVENTORY_ACTIONS.DELETE_ASSET:
      return {
        ...state,
        assets: state.assets.filter(asset => asset._id !== action.payload),
      };
      
    // Audits
    case INVENTORY_ACTIONS.SET_AUDITS:
      return {
        ...state,
        audits: action.payload.data,
        pagination: {
          ...state.pagination,
          audits: {
            ...state.pagination.audits,
            ...action.payload.pagination,
          },
        },
      };
      
    case INVENTORY_ACTIONS.ADD_AUDIT:
      return {
        ...state,
        audits: [action.payload, ...state.audits],
      };
      
    case INVENTORY_ACTIONS.UPDATE_AUDIT:
      return {
        ...state,
        audits: state.audits.map(audit =>
          audit._id === action.payload._id ? action.payload : audit
        ),
      };
      
    // Filters and pagination
    case INVENTORY_ACTIONS.SET_FILTERS:
      return {
        ...state,
        filters: {
          ...state.filters,
          [action.payload.key]: action.payload.filters,
        },
      };
      
    case INVENTORY_ACTIONS.SET_PAGINATION:
      return {
        ...state,
        pagination: {
          ...state.pagination,
          [action.payload.key]: {
            ...state.pagination[action.payload.key],
            ...action.payload.pagination,
          },
        },
      };
      
    default:
      return state;
  }
}

// Provider component
export function InventoryProvider({ children }) {
  const [state, dispatch] = useReducer(inventoryReducer, initialState);

  // Helper function to handle API calls with loading and error states
  const handleApiCall = useCallback(async (apiCall, loadingKey) => {
    try {
      dispatch({ type: INVENTORY_ACTIONS.SET_LOADING, payload: { key: loadingKey, value: true } });
      dispatch({ type: INVENTORY_ACTIONS.CLEAR_ERROR });
      
      const result = await apiCall();
      
      dispatch({ type: INVENTORY_ACTIONS.SET_LOADING, payload: { key: loadingKey, value: false } });
      return result;
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'An error occurred';
      dispatch({ type: INVENTORY_ACTIONS.SET_ERROR, payload: message });
      throw error;
    }
  }, []);

  // Categories actions
  const fetchCategories = useCallback(async (params = {}) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.getCategories(params);
      dispatch({ type: INVENTORY_ACTIONS.SET_CATEGORIES, payload: response });
      return response;
    }, 'categories');
  }, [handleApiCall]);

  const fetchCategoryTree = useCallback(async () => {
    return handleApiCall(async () => {
      const response = await inventoryApi.getCategoryTree();
      dispatch({ type: INVENTORY_ACTIONS.SET_CATEGORY_TREE, payload: response.data });
      return response;
    }, 'categories');
  }, [handleApiCall]);

  const createCategory = useCallback(async (categoryData) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.createCategory(categoryData);
      dispatch({ type: INVENTORY_ACTIONS.ADD_CATEGORY, payload: response.data });
      return response;
    }, 'categories');
  }, [handleApiCall]);

  const updateCategory = useCallback(async (id, categoryData) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.updateCategory(id, categoryData);
      dispatch({ type: INVENTORY_ACTIONS.UPDATE_CATEGORY, payload: response.data });
      return response;
    }, 'categories');
  }, [handleApiCall]);

  const deleteCategory = useCallback(async (id) => {
    return handleApiCall(async () => {
      await inventoryApi.deleteCategory(id);
      dispatch({ type: INVENTORY_ACTIONS.DELETE_CATEGORY, payload: id });
    }, 'categories');
  }, [handleApiCall]);

  // Items actions
  const fetchItems = useCallback(async (params = {}) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.getItems(params);
      dispatch({ type: INVENTORY_ACTIONS.SET_ITEMS, payload: response });
      return response;
    }, 'items');
  }, [handleApiCall]);

  const fetchItemById = useCallback(async (id) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.getItemById(id);
      dispatch({ type: INVENTORY_ACTIONS.SET_SELECTED_ITEM, payload: response.data });
      return response;
    }, 'items');
  }, [handleApiCall]);

  const createItem = useCallback(async (itemData) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.createItem(itemData);
      dispatch({ type: INVENTORY_ACTIONS.ADD_ITEM, payload: response.data });
      return response;
    }, 'items');
  }, [handleApiCall]);

  const updateItem = useCallback(async (id, itemData) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.updateItem(id, itemData);
      dispatch({ type: INVENTORY_ACTIONS.UPDATE_ITEM, payload: response.data });
      return response;
    }, 'items');
  }, [handleApiCall]);

  const deleteItem = useCallback(async (id) => {
    return handleApiCall(async () => {
      await inventoryApi.deleteItem(id);
      dispatch({ type: INVENTORY_ACTIONS.DELETE_ITEM, payload: id });
    }, 'items');
  }, [handleApiCall]);

  const searchItems = useCallback(async (searchTerm) => {
    return handleApiCall(async () => {
      return await inventoryApi.searchItems(searchTerm);
    }, 'items');
  }, [handleApiCall]);

  // Suppliers actions
  const fetchSuppliers = useCallback(async (params = {}) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.getSuppliers(params);
      dispatch({ type: INVENTORY_ACTIONS.SET_SUPPLIERS, payload: response });
      return response;
    }, 'suppliers');
  }, [handleApiCall]);

  const createSupplier = useCallback(async (supplierData) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.createSupplier(supplierData);
      dispatch({ type: INVENTORY_ACTIONS.ADD_SUPPLIER, payload: response.data });
      return response;
    }, 'suppliers');
  }, [handleApiCall]);

  const updateSupplier = useCallback(async (id, supplierData) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.updateSupplier(id, supplierData);
      dispatch({ type: INVENTORY_ACTIONS.UPDATE_SUPPLIER, payload: response.data });
      return response;
    }, 'suppliers');
  }, [handleApiCall]);

  const deleteSupplier = useCallback(async (id) => {
    return handleApiCall(async () => {
      await inventoryApi.deleteSupplier(id);
      dispatch({ type: INVENTORY_ACTIONS.DELETE_SUPPLIER, payload: id });
    }, 'suppliers');
  }, [handleApiCall]);

  // Warehouses actions (now using location API)
  const fetchWarehouses = useCallback(async (params = {}) => {
    return handleApiCall(async () => {
      try {
        // Get locations filtered by warehouse type
        const response = await adminApi.getLocations({ type: 'warehouse', ...params });
        const body = response.data || {};
        const warehousesData = Array.isArray(body.data) ? body.data : (Array.isArray(body) ? body : []);
        const normalized = { ...body, data: warehousesData };
        dispatch({ type: INVENTORY_ACTIONS.SET_WAREHOUSES, payload: normalized });
        return normalized;
      } catch (error) {
        console.error('Error fetching warehouses, trying fallback without type filter:', error);
        // Fallback: try to get all locations and filter client-side
        const locationsResponse = await adminApi.getLocations(params);
        const body = locationsResponse.data || {};
        const warehousesData = Array.isArray(body.data) ? body.data : (Array.isArray(body) ? body : []);
        const filteredWarehouses = warehousesData.filter(location => location.type === 'warehouse');
        const fallbackResponse = { ...body, data: filteredWarehouses };
        dispatch({ type: INVENTORY_ACTIONS.SET_WAREHOUSES, payload: fallbackResponse });
        return fallbackResponse;
      }
    }, 'warehouses');
  }, [handleApiCall]);

  // Stock actions
  const fetchStockLevels = useCallback(async (params = {}) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.getStockLevels(params);
      dispatch({ type: INVENTORY_ACTIONS.SET_STOCK_LEVELS, payload: response });
      return response;
    }, 'stock');
  }, [handleApiCall]);

  const updateStockLevel = useCallback(async (stockData) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.updateStockLevel(stockData);
      dispatch({ type: INVENTORY_ACTIONS.UPDATE_STOCK_LEVEL, payload: response.data });
      return response;
    }, 'stock');
  }, [handleApiCall]);

  const transferStock = useCallback(async (transferData) => {
    return handleApiCall(async () => {
      return await inventoryApi.transferStock(transferData);
    }, 'stock');
  }, [handleApiCall]);

  const fetchLowStockItems = useCallback(async (params = {}) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.getLowStockItems(params);
      dispatch({ type: INVENTORY_ACTIONS.SET_LOW_STOCK_ITEMS, payload: response.data });
      return response;
    }, 'stock');
  }, [handleApiCall]);

  // Movements actions
  const fetchMovements = useCallback(async (params = {}) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.getMovements(params);
      dispatch({ type: INVENTORY_ACTIONS.SET_MOVEMENTS, payload: response });
      return response;
    }, 'movements');
  }, [handleApiCall]);

  const createMovement = useCallback(async (movementData) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.createMovement(movementData);
      dispatch({ type: INVENTORY_ACTIONS.ADD_MOVEMENT, payload: response.data });
      return response;
    }, 'movements');
  }, [handleApiCall]);

  const approveMovement = useCallback(async (id, approvalData) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.approveMovement(id, approvalData);
      dispatch({ type: INVENTORY_ACTIONS.UPDATE_MOVEMENT, payload: response.data });
      return response;
    }, 'movements');
  }, [handleApiCall]);

  // Purchase Orders actions
  const fetchPurchaseOrders = useCallback(async (params = {}) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.getPurchaseOrders(params);
      dispatch({ type: INVENTORY_ACTIONS.SET_PURCHASE_ORDERS, payload: response });
      return response;
    }, 'purchaseOrders');
  }, [handleApiCall]);

  const createPurchaseOrder = useCallback(async (orderData) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.createPurchaseOrder(orderData);
      dispatch({ type: INVENTORY_ACTIONS.ADD_PURCHASE_ORDER, payload: response.data });
      return response;
    }, 'purchaseOrders');
  }, [handleApiCall]);

  const updatePurchaseOrder = useCallback(async (id, orderData) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.updatePurchaseOrder(id, orderData);
      dispatch({ type: INVENTORY_ACTIONS.UPDATE_PURCHASE_ORDER, payload: response.data });
      return response;
    }, 'purchaseOrders');
  }, [handleApiCall]);

  // ============================================================================
  // GOODS TRANSFER REQUESTS ACTIONS
  // ============================================================================

  const fetchGoodsTransferRequests = useCallback(async (params = {}) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.getGoodsTransferRequests(params);
      dispatch({ type: INVENTORY_ACTIONS.SET_GOODS_TRANSFER_REQUESTS, payload: response });
      return response;
    }, 'goodsTransferRequests');
  }, [handleApiCall]);

  const getGoodsTransferRequestById = useCallback(async (id) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.getGoodsTransferRequestById(id);
      return response;
    }, 'goodsTransferRequests');
  }, [handleApiCall]);

  const createGoodsTransferRequest = useCallback(async (gtrData) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.createGoodsTransferRequest(gtrData);
      dispatch({ type: INVENTORY_ACTIONS.ADD_GOODS_TRANSFER_REQUEST, payload: response.data });
      return response;
    }, 'goodsTransferRequests');
  }, [handleApiCall]);

  const updateGoodsTransferRequest = useCallback(async (id, gtrData) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.updateGoodsTransferRequest(id, gtrData);
      dispatch({ type: INVENTORY_ACTIONS.UPDATE_GOODS_TRANSFER_REQUEST, payload: response.data });
      return response;
    }, 'goodsTransferRequests');
  }, [handleApiCall]);

  const deleteGoodsTransferRequest = useCallback(async (id) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.deleteGoodsTransferRequest(id);
      dispatch({ type: INVENTORY_ACTIONS.DELETE_GOODS_TRANSFER_REQUEST, payload: id });
      return response;
    }, 'goodsTransferRequests');
  }, [handleApiCall]);

  const submitGoodsTransferRequestForApproval = useCallback(async (id) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.submitGoodsTransferRequestForApproval(id);
      dispatch({ type: INVENTORY_ACTIONS.UPDATE_GOODS_TRANSFER_REQUEST, payload: response.data });
      return response;
    }, 'goodsTransferRequests');
  }, [handleApiCall]);

  const approveGoodsTransferRequest = useCallback(async (id, approvalData = {}) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.approveGoodsTransferRequest(id, approvalData);
      dispatch({ type: INVENTORY_ACTIONS.UPDATE_GOODS_TRANSFER_REQUEST, payload: response.data });
      return response;
    }, 'goodsTransferRequests');
  }, [handleApiCall]);

  const rejectGoodsTransferRequest = useCallback(async (id, rejectionData) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.rejectGoodsTransferRequest(id, rejectionData);
      dispatch({ type: INVENTORY_ACTIONS.UPDATE_GOODS_TRANSFER_REQUEST, payload: response.data });
      return response;
    }, 'goodsTransferRequests');
  }, [handleApiCall]);

  const cancelGoodsTransferRequest = useCallback(async (id, reason) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.cancelGoodsTransferRequest(id, reason);
      dispatch({ type: INVENTORY_ACTIONS.UPDATE_GOODS_TRANSFER_REQUEST, payload: response.data });
      return response;
    }, 'goodsTransferRequests');
  }, [handleApiCall]);

  const getPendingGoodsTransferRequestApprovals = useCallback(async () => {
    return handleApiCall(async () => {
      const response = await inventoryApi.getPendingGoodsTransferRequestApprovals();
      return response;
    }, 'goodsTransferRequests');
  }, [handleApiCall]);

  const getGoodsTransferRequestSummary = useCallback(async (params = {}) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.getGoodsTransferRequestSummary(params);
      return response;
    }, 'goodsTransferRequests');
  }, [handleApiCall]);

  const addGoodsTransferRequestNote = useCallback(async (id, noteData) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.addGoodsTransferRequestNote(id, noteData);
      return response;
    }, 'goodsTransferRequests');
  }, [handleApiCall]);

  const updateGoodsTransferProgress = useCallback(async (id, lineItemId, transferredQuantity) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.updateGoodsTransferProgress(id, lineItemId, transferredQuantity);
      dispatch({ type: INVENTORY_ACTIONS.UPDATE_GOODS_TRANSFER_REQUEST, payload: response.data });
      return response;
    }, 'goodsTransferRequests');
  }, [handleApiCall]);

  const getGTRStockMovements = useCallback(async (id) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.getGTRStockMovements(id);
      return response;
    }, 'goodsTransferRequests');
  }, [handleApiCall]);

  const getGTRToIssueCount = useCallback(async () => {
    return handleApiCall(async () => {
      const response = await inventoryApi.getGTRToIssueCount();
      return response;
    }, 'goodsTransferRequests');
  }, [handleApiCall]);

  // NEW: Confirm receipt at destination warehouse (Two-Stage Approval)
  const confirmGoodsTransferReceipt = useCallback(async (id, receiptData = {}) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.confirmGoodsTransferReceipt(id, receiptData);
      dispatch({ type: INVENTORY_ACTIONS.UPDATE_GOODS_TRANSFER_REQUEST, payload: response.data });
      return response;
    }, 'goodsTransferRequests');
  }, [handleApiCall]);

  // NEW: Reject receipt at destination warehouse (Two-Stage Approval)
  const rejectGoodsTransferReceipt = useCallback(async (id, rejectionData) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.rejectGoodsTransferReceipt(id, rejectionData);
      dispatch({ type: INVENTORY_ACTIONS.UPDATE_GOODS_TRANSFER_REQUEST, payload: response.data });
      return response;
    }, 'goodsTransferRequests');
  }, [handleApiCall]);

  // Goods Issue Notes actions
  const fetchGoodsIssueNotes = useCallback(async (params = {}) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.getGoodsIssueNotes(params);
      dispatch({ type: INVENTORY_ACTIONS.SET_GOODS_ISSUE_NOTES, payload: response });
      return response;
    }, 'goodsIssueNotes');
  }, [handleApiCall]);

  const getGoodsIssueNoteById = useCallback(async (id) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.getGoodsIssueNoteById(id);
      return response;
    }, 'goodsIssueNotes');
  }, [handleApiCall]);

  const createGoodsIssueNote = useCallback(async (data) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.createGoodsIssueNote(data);
      dispatch({ type: INVENTORY_ACTIONS.ADD_GOODS_ISSUE_NOTE, payload: response.data });
      return response;
    }, 'goodsIssueNotes');
  }, [handleApiCall]);

  const updateGoodsIssueNote = useCallback(async (id, data) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.updateGoodsIssueNote(id, data);
      dispatch({ type: INVENTORY_ACTIONS.UPDATE_GOODS_ISSUE_NOTE, payload: response.data });
      return response;
    }, 'goodsIssueNotes');
  }, [handleApiCall]);

  const deleteGoodsIssueNote = useCallback(async (id) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.deleteGoodsIssueNote(id);
      dispatch({ type: INVENTORY_ACTIONS.DELETE_GOODS_ISSUE_NOTE, payload: id });
      return response;
    }, 'goodsIssueNotes');
  }, [handleApiCall]);

  const submitGoodsIssueNoteForApproval = useCallback(async (id) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.submitGoodsIssueNoteForApproval(id);
      dispatch({ type: INVENTORY_ACTIONS.UPDATE_GOODS_ISSUE_NOTE, payload: response.data });
      return response;
    }, 'goodsIssueNotes');
  }, [handleApiCall]);

  // Approving a GIN executes the dispatch half of the transfer (source decrement, staged
  // as in-transit) and auto-creates a Transfer In record at the destination — it no longer
  // credits destination stock directly. Response shape is { goodsIssueNote, transferIn }.
  // Also patch the underlying GTR entry, if already loaded in state, since its status just
  // changed as a side effect, and register the new Transfer In in state if it's loaded.
  const approveGoodsIssueNote = useCallback(async (id) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.approveGoodsIssueNote(id);
      const { goodsIssueNote, transferIn } = response.data || {};
      if (goodsIssueNote) {
        dispatch({ type: INVENTORY_ACTIONS.UPDATE_GOODS_ISSUE_NOTE, payload: goodsIssueNote });
      }
      if (transferIn) {
        dispatch({ type: INVENTORY_ACTIONS.ADD_TRANSFER_IN, payload: transferIn });
      }

      const updatedGtrRef = goodsIssueNote?.gtr;
      const gtrId = updatedGtrRef?._id || updatedGtrRef;
      if (gtrId) {
        const existingGTR = state.goodsTransferRequests.find(g => g._id === gtrId);
        if (existingGTR && updatedGtrRef?.status) {
          dispatch({ type: INVENTORY_ACTIONS.UPDATE_GOODS_TRANSFER_REQUEST, payload: { ...existingGTR, status: updatedGtrRef.status } });
        }
      }
      return response;
    }, 'goodsIssueNotes');
  }, [handleApiCall, state.goodsTransferRequests]);

  const rejectGoodsIssueNote = useCallback(async (id, reason) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.rejectGoodsIssueNote(id, reason);
      dispatch({ type: INVENTORY_ACTIONS.UPDATE_GOODS_ISSUE_NOTE, payload: response.data });
      return response;
    }, 'goodsIssueNotes');
  }, [handleApiCall]);

  // Transfer In actions
  const fetchTransferIns = useCallback(async (params = {}) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.getTransferIns(params);
      dispatch({ type: INVENTORY_ACTIONS.SET_TRANSFER_INS, payload: response });
      return response;
    }, 'transferIns');
  }, [handleApiCall]);

  const getTransferInById = useCallback(async (id) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.getTransferInById(id);
      return response;
    }, 'transferIns');
  }, [handleApiCall]);

  const updateTransferIn = useCallback(async (id, data) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.updateTransferIn(id, data);
      dispatch({ type: INVENTORY_ACTIONS.UPDATE_TRANSFER_IN, payload: response.data });
      return response;
    }, 'transferIns');
  }, [handleApiCall]);

  // Approving a Transfer In is what finally credits destination stock — also patch the
  // underlying GTR entry, if already loaded in state, since its status/line items just
  // changed as a side effect.
  const approveTransferIn = useCallback(async (id, data = {}) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.approveTransferIn(id, data);
      const { transferIn, gtr } = response.data || {};
      if (transferIn) {
        dispatch({ type: INVENTORY_ACTIONS.UPDATE_TRANSFER_IN, payload: transferIn });
      }
      if (gtr?._id) {
        const existingGTR = state.goodsTransferRequests.find(g => g._id === gtr._id);
        if (existingGTR) {
          dispatch({ type: INVENTORY_ACTIONS.UPDATE_GOODS_TRANSFER_REQUEST, payload: { ...existingGTR, status: gtr.status } });
        }
      }
      return response;
    }, 'transferIns');
  }, [handleApiCall, state.goodsTransferRequests]);

  const rejectTransferIn = useCallback(async (id, reason) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.rejectTransferIn(id, reason);
      const { transferIn } = response.data || {};
      if (transferIn) {
        dispatch({ type: INVENTORY_ACTIONS.UPDATE_TRANSFER_IN, payload: transferIn });
      }
      return response;
    }, 'transferIns');
  }, [handleApiCall]);

  // Dashboard actions
  const fetchDashboardData = useCallback(async (params = {}) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.getDashboardData(params);
      dispatch({ type: INVENTORY_ACTIONS.SET_DASHBOARD_DATA, payload: response.data });
      return response;
    }, 'dashboard');
  }, [handleApiCall]);

  // Assets actions
  const fetchAssets = useCallback(async (params = {}) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.getAssets(params);
      dispatch({ type: INVENTORY_ACTIONS.SET_ASSETS, payload: response });
      return response;
    }, 'assets');
  }, [handleApiCall]);

  const createAsset = useCallback(async (assetData) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.createAsset(assetData);
      dispatch({ type: INVENTORY_ACTIONS.ADD_ASSET, payload: response.data });
      return response;
    }, 'assets');
  }, [handleApiCall]);

  const updateAsset = useCallback(async (id, assetData) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.updateAsset(id, assetData);
      dispatch({ type: INVENTORY_ACTIONS.UPDATE_ASSET, payload: response.data });
      return response;
    }, 'assets');
  }, [handleApiCall]);

  // Audits actions
  const fetchAudits = useCallback(async (params = {}) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.getAudits(params);
      dispatch({ type: INVENTORY_ACTIONS.SET_AUDITS, payload: response });
      return response;
    }, 'audits');
  }, [handleApiCall]);

  const createAudit = useCallback(async (auditData) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.createAudit(auditData);
      dispatch({ type: INVENTORY_ACTIONS.ADD_AUDIT, payload: response.data });
      return response;
    }, 'audits');
  }, [handleApiCall]);

  const updateAudit = useCallback(async (id, auditData) => {
    return handleApiCall(async () => {
      const response = await inventoryApi.updateAudit(id, auditData);
      dispatch({ type: INVENTORY_ACTIONS.UPDATE_AUDIT, payload: response.data });
      return response;
    }, 'audits');
  }, [handleApiCall]);

  // Filter and pagination actions
  const setFilters = useCallback((key, filters) => {
    dispatch({ type: INVENTORY_ACTIONS.SET_FILTERS, payload: { key, filters } });
  }, []);

  const setPagination = useCallback((key, pagination) => {
    dispatch({ type: INVENTORY_ACTIONS.SET_PAGINATION, payload: { key, pagination } });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: INVENTORY_ACTIONS.CLEAR_ERROR });
  }, []);

  const value = {
    // State
    ...state,
    
    // Actions
    // Categories
    fetchCategories,
    fetchCategoryTree,
    createCategory,
    updateCategory,
    deleteCategory,
    
    // Items
    fetchItems,
    fetchItemById,
    createItem,
    updateItem,
    deleteItem,
    searchItems,
    
    // Suppliers
    fetchSuppliers,
    createSupplier,
    updateSupplier,
    deleteSupplier,
    
    // Warehouses (read-only from location service)
    fetchWarehouses,
    
    // Stock
    fetchStockLevels,
    updateStockLevel,
    transferStock,
    fetchLowStockItems,
    
    // Movements
    fetchMovements,
    createMovement,
    approveMovement,
    
    // Purchase Orders
    fetchPurchaseOrders,
    createPurchaseOrder,
    updatePurchaseOrder,
    
    // Goods Transfer Requests
    goodsTransferRequests: state.goodsTransferRequests,
    fetchGoodsTransferRequests,
    getGoodsTransferRequestById,
    createGoodsTransferRequest,
    updateGoodsTransferRequest,
    deleteGoodsTransferRequest,
    submitGoodsTransferRequestForApproval,
    approveGoodsTransferRequest,
    rejectGoodsTransferRequest,
    cancelGoodsTransferRequest,
    getPendingGoodsTransferRequestApprovals,
    getGoodsTransferRequestSummary,
    addGoodsTransferRequestNote,
    updateGoodsTransferProgress,
    getGTRStockMovements,
    getGTRToIssueCount,
    confirmGoodsTransferReceipt,      // NEW
    rejectGoodsTransferReceipt,       // NEW

    // Goods Issue Notes
    goodsIssueNotes: state.goodsIssueNotes,
    fetchGoodsIssueNotes,
    getGoodsIssueNoteById,
    createGoodsIssueNote,
    updateGoodsIssueNote,
    deleteGoodsIssueNote,
    submitGoodsIssueNoteForApproval,
    approveGoodsIssueNote,
    rejectGoodsIssueNote,

    // Transfer In
    transferIns: state.transferIns,
    fetchTransferIns,
    getTransferInById,
    updateTransferIn,
    approveTransferIn,
    rejectTransferIn,

    // Dashboard
    fetchDashboardData,
    
    // Assets
    fetchAssets,
    createAsset,
    updateAsset,
    
    // Audits
    fetchAudits,
    createAudit,
    updateAudit,
    
    // Utilities
    setFilters,
    setPagination,
    clearError,
    
    // Direct API access
    inventoryApi,
  };

  return (
    <InventoryContext.Provider value={value}>
      {children}
    </InventoryContext.Provider>
  );
}

// Custom hook to use inventory context
export function useInventory() {
  return useContext(InventoryContext);
}

export default InventoryContext;